import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import FunctionalButton from "components/layout/FunctionalButton";
import { useGetBehaviourMissionAudioQuery } from "app/services/behaviourRulesApi";
import { startMissionAudio, stopMissionAudio } from "lib/audioTracksApi";
import { useMissionExecution } from "providers/navigation/MissionExecutionProvider";
import { useToast } from "providers/ToastProvider";
import useFleetArcId from "lib/utils/useFleetArcId";

const TERMINAL_MISSION_STATUSES = new Set([
    "idle",
    "completed",
    "failed",
    "cancelled",
    "canceled",
    "succeeded",
]);

const MuteIcon = ({ muted }) => (
    <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 9v6h4l5 5V4L8 9H4z" fill="currentColor" />
        {muted ? (
            <path
                d="M16.5 8.5l5 5m0-5l-5 5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />
        ) : (
            <path
                d="M16 8a5 5 0 010 8"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
            />
        )}
    </svg>
);

/**
 * During an active mission with configured audio: mute/unmute control to the
 * left of record/gallery. Light/flash stay on the left BottomBar controls.
 */
const MissionOverrideButtons = () => {
    const { t } = useTranslation("control");
    const { addToast } = useToast();
    const { isExecuting, currentMissionId, executorState } = useMissionExecution();
    const arcId = useFleetArcId();
    const { arcProxyHost } = useSelector((state) => state.arcProxy);
    const [muted, setMuted] = useState(false);
    const [busy, setBusy] = useState(false);

    const normalizedExecutorStatus = String(executorState?.status || "")
        .trim()
        .toLowerCase();
    const normalizedExecutorMissionId = String(executorState?.mission_id || "").trim();
    const isExecutorMissionActive =
        Boolean(normalizedExecutorMissionId) &&
        !TERMINAL_MISSION_STATUSES.has(normalizedExecutorStatus);
    const activeMissionId =
        (isExecutorMissionActive && normalizedExecutorMissionId) ||
        (isExecuting && String(currentMissionId || "").trim()) ||
        null;

    const { data: missionAudioData } = useGetBehaviourMissionAudioQuery(activeMissionId, {
        skip: !activeMissionId,
    });

    const missionAudio = missionAudioData?.mission_audio;
    const audioEnabled = useMemo(() => {
        if (!missionAudio || typeof missionAudio !== "object") return false;
        const trackId = String(missionAudio.track_id || "").trim();
        if (!trackId) return false;
        if (missionAudio.enabled === false) return false;
        return true;
    }, [missionAudio]);

    useEffect(() => {
        setMuted(false);
        setBusy(false);
    }, [activeMissionId]);

    if (!activeMissionId || !audioEnabled) {
        return null;
    }

    const toggleMute = async () => {
        if (busy || !missionAudio) return;
        const trackId = String(missionAudio.track_id || "").trim();
        if (!trackId) return;
        setBusy(true);
        try {
            if (!muted) {
                await stopMissionAudio(arcId, arcProxyHost);
                setMuted(true);
            } else {
                const intervalSec = Number(missionAudio.interval_sec) || 30;
                const volume = Number(missionAudio.volume) || 80;
                await startMissionAudio(trackId, intervalSec, volume, arcId, arcProxyHost);
                setMuted(false);
            }
        } catch (err) {
            addToast({
                type: "warning",
                message:
                    err instanceof Error
                        ? err.message
                        : muted
                            ? t("bottomBar.unmuteFailed")
                            : t("bottomBar.muteFailed"),
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <FunctionalButton
            className={twMerge(
                "relative z-10 control-button-type3 shrink-0 text-white transition-all duration-ios-open ease-ios",
                muted && "scale-95 bg-primary2 text-on-accent",
                busy && "control-button-disable"
            )}
            icon={<MuteIcon muted={muted} />}
            hoverText={
                muted ? t("bottomBar.unmuteMissionAudio") : t("bottomBar.muteMissionAudio")
            }
            disabled={busy}
            onClick={() => void toggleMute()}
        />
    );
};

export default MissionOverrideButtons;
